import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Loader2, Upload, FileText, CheckCircle2, Download, AlertTriangle } from 'lucide-react';
import AdmissaoStepper from './AdmissaoStepper';

const API = process.env.REACT_APP_BACKEND_URL;

const RHAdmissaoExamePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [processo, setProcesso] = useState(null);
  const [camposFaltantes, setCamposFaltantes] = useState([]);
  const [concluding, setConcluding] = useState(false);
  const [form, setForm] = useState({ data: '', horario: '', clinica_nome: '', endereco: '', mapa_link: '', orientacoes: '' });
  const fileInputRef = useRef(null);

  const loadProcesso = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const { processo: p, fields } = response.data;
      if (p.process_type_slug !== 'admissao') {
        toast.error('Etapa de exame admissional disponível apenas para processos de admissão');
        navigate(`/rh/processos/${id}`);
        return;
      }
      setProcesso(p);
      setCamposFaltantes((fields || []).filter(f => f.required && !p.fields_data?.[f.field_key]));
      const exame = p.exame_admissional || {};
      setForm({
        data: exame.data || '',
        horario: exame.horario || '',
        clinica_nome: exame.clinica_nome || '',
        endereco: exame.endereco || '',
        mapa_link: exame.mapa_link || '',
        orientacoes: exame.orientacoes || '',
      });
    } catch (error) {
      toast.error('Erro ao carregar processo');
      navigate('/rh/admissoes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadProcesso(); }, [loadProcesso]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.data || !form.horario || !form.clinica_nome || !form.endereco) {
      toast.error('Preencha data, horário, clínica e endereço do exame');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/api/rh/processos/${id}/exame-admissional`, form, { headers: { Authorization: `Bearer ${token}` } });
      setProcesso(response.data);
      toast.success('Exame admissional salvo e colaborador notificado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar exame admissional');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAso = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/rh/processos/${id}/exame-admissional/aso`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setProcesso(response.data);
      toast.success('ASO anexado! Colaborador notificado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar ASO');
    } finally {
      setUploading(false);
    }
  };

  const handleConcluir = async () => {
    if (!window.confirm('Concluir a admissão? O cadastro do funcionário será criado. Esta ação não pode ser desfeita.')) return;
    try {
      setConcluding(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rh/processos/${id}/concluir`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Admissão concluída com sucesso!');
      navigate('/rh/admissoes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir admissão');
    } finally {
      setConcluding(false);
    }
  };

  const handleDownloadAso = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${id}/documentos/${exame.aso_document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ASO_Admissional';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar ASO');
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (!processo) return null;

  if (!processo.etapa_documentos_concluida) {
    return (
      <div className="p-6 max-w-xl mx-auto space-y-4">
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">Etapa de documentos pendente</AlertTitle>
          <AlertDescription className="text-amber-800">
            Conclua a solicitação/anexação de documentos do colaborador antes de configurar o exame admissional.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(`/rh/processos/${id}`)}>Voltar ao processo</Button>
      </div>
    );
  }

  const exame = processo.exame_admissional || { status: 'pendente' };
  const exameAgendado = exame.status === 'agendado' || exame.status === 'concluido';
  const exameConcluido = exame.status === 'concluido';

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurar Exame Admissional</h1>
          <p className="text-sm text-gray-500">Agende o exame e informe os detalhes para o colaborador.</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/rh/processos/${id}`)}>Voltar</Button>
      </div>

      <AdmissaoStepper currentStep={2} />

      <Card>
        <CardHeader>
          <CardTitle>Dados do exame</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Data do exame</Label>
              <Input type="date" value={form.data} onChange={(e) => setField('data', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Horário</Label>
              <Input type="time" value={form.horario} onChange={(e) => setField('horario', e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Nome da clínica</Label>
            <Input value={form.clinica_nome} onChange={(e) => setField('clinica_nome', e.target.value)} placeholder="Clínica Ocupacional" />
          </div>
          <div className="space-y-1">
            <Label>Endereço completo</Label>
            <Input value={form.endereco} onChange={(e) => setField('endereco', e.target.value)} placeholder="Av. Exemplo, 123 - Centro - São Paulo / SP - CEP 01000-000" />
          </div>
          <div className="space-y-1">
            <Label>Link do mapa (opcional)</Label>
            <Input value={form.mapa_link} onChange={(e) => setField('mapa_link', e.target.value)} placeholder="https://maps.google.com/?q=Clinica+Ocupacional" />
          </div>
          <div className="space-y-1">
            <Label>Orientações ao candidato</Label>
            <Textarea
              value={form.orientacoes}
              onChange={(e) => setField('orientacoes', e.target.value)}
              placeholder="Leve um documento oficial com foto. Jejum não obrigatório. Chegue com 15 minutos de antecedência."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {camposFaltantes.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">Campos obrigatórios pendentes</AlertTitle>
          <AlertDescription className="text-amber-800">
            <p>Preencha antes de concluir a admissão: {camposFaltantes.map(f => f.field_label).join(', ')}.</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(`/rh/processos/${id}/editar`)}>
              Preencher agora
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Anexar ASO</CardTitle>
          <CardDescription>Após o exame, o RH deverá anexar o ASO para disponibilizá-lo ao colaborador.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {exameConcluido ? (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-sm text-emerald-800">ASO Admissional anexado</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadAso}>
                <Download className="h-4 w-4 mr-1" /> Baixar
              </Button>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
              } ${!exameAgendado ? 'opacity-50' : 'cursor-pointer'}`}
              onDragOver={(e) => { e.preventDefault(); if (exameAgendado) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (exameAgendado) handleUploadAso(e.dataTransfer.files?.[0]);
              }}
              onClick={() => exameAgendado && fileInputRef.current?.click()}
            >
              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Arraste o ASO ou clique para selecionar</p>
              <p className="text-xs text-gray-400 mt-1">PDF, JPG ou PNG - máximo 10MB</p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleUploadAso(e.target.files?.[0])}
              />
              <Button className="mt-4" size="sm" disabled={!exameAgendado || uploading} onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Selecionar arquivo
              </Button>
              {!exameAgendado && (
                <p className="text-xs text-amber-600 mt-2">Salve os dados do exame antes de anexar o ASO.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate(`/rh/processos/${id}`)}>Cancelar</Button>
        <div className="flex items-center gap-2">
          {!exameConcluido && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar e notificar colaborador
            </Button>
          )}
          {exameConcluido && (
            <Button onClick={handleConcluir} disabled={concluding || camposFaltantes.length > 0} title={camposFaltantes.length > 0 ? 'Preencha os campos obrigatórios pendentes antes de concluir' : undefined}>
              {concluding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Concluir Admissão
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RHAdmissaoExamePage;
